import React from 'react';
import { Link } from 'gatsby';
import marked from 'marked';

import CDNImage from '../../components/common/cdn-image';
import IndexSidebar from './index-sidebar';
import { LinkSvg, ShoppingBagSvg, ExternalLinkSvg } from '../../components/common/svg';

import '../../styles/essays/formatted-essay.scss';
import '../../styles/print-styles.scss';

export default class FormattedEssay extends React.Component {
	constructor(props) {
		super(props);

		this.buildImage = this.buildImage.bind(this);
		this.buildSideBySide = this.buildSideBySide.bind(this);
		this.grabFootnoteReferences = this.grabFootnoteReferences.bind(this);
		this.insertFootnoteAnchors = this.insertFootnoteAnchors.bind(this);
		this.processTextBlock = this.processTextBlock.bind(this);
		this.processedEssay = this.processedEssay.bind(this);

		this.footnoteReferences = this.grabFootnoteReferences(props.essayText);
	}

	componentDidMount() {
		const indexButtons = [...document.querySelectorAll('.index-button')];

		indexButtons.forEach(button => {
			button.addEventListener('click', () => {
				const paragraphReference = button.id.split('index-button-')[1];

				const indexLinkBlock = document.getElementById(`index-sidebar-p-${paragraphReference}`)

				if (indexLinkBlock.classList.contains('expanded')) {
					indexLinkBlock.classList.remove('expanded');
				} else {
					indexLinkBlock.classList.add('expanded');
				}
			});
		});
	}

	buildSideBySide(data, i) {
		const sideRegex = /side={([\S]+)}/;
		let side = 'left';
		if (data.match(sideRegex)) {
			side = data.match(sideRegex)[1];
		}

        const evenSplitRegex = /evenSplit={([\S]+)}/;
        const evenSplit = data.match(evenSplitRegex);

		const imageRegex = /image={([\S]+?)}/;
		let image = undefined;
		if (data.match(imageRegex)) {
			image = data.match(imageRegex)[1];
		}

        const captionRegex = /caption={([\s\S]+?)}/
        let caption = undefined
        if (data.match(captionRegex)) {
          caption = data.match(captionRegex)[1]
        }

		const imageAltRegex = /alt={([\s\S]+?)}/;
		let alt = undefined;
		if (data.match(imageAltRegex)) {
			alt = data.match(imageAltRegex)[1];
		}

		const textRegex = /text={([\s\S]+?)}/;
		let text = undefined;
		if (data.match(textRegex)) {
			text = data.match(textRegex)[1];
		}

		const imageContents = (
			<>
				<CDNImage imagePath={image} alt={alt} fit={'max'} width={640} showUndownloadedMessage={ true } />
			</>
		);

		return (
			<div id={`side-by-side-${i}`} className={`essay__side-by-side essay__side-by-side__${side} ${evenSplit ? 'essay__side-by-side__even-split' : ''}`}>
				<div className="essay__side-by-side__image">
					{imageContents}
                    {caption && (
                        <p
                            className="caption"
                            dangerouslySetInnerHTML={{ __html: marked.parseInline(caption) }}
                        />
                    )}
				</div>
				<div
					className="essay__side-by-side__text"
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			</div>
		);
	}

	buildImage(data, i) {
		const captionRegex = /caption={([\s\S]*?)}/
		let caption = ""
		if (data.match(captionRegex)) {
			caption = data.match(captionRegex)[1]
		}

		return (
			<div id={`img-${i}`} className="essay__image">
				<div className="essay__center-content">
					<div className="essay__image__image">
						<a className="essay__anchor-link" href={`#img-${i}`}>{LinkSvg}</a>
						{this.formatImage(data)}
					</div>
					{(caption.length) ? (
						<div
							className="essay__image__caption"
							dangerouslySetInnerHTML={{ __html: marked(caption) }}
						/>
					) : null}
				</div>
			</div>
		);
	};

	formatImage(data) {
		const pathRegex = /path={([\S]+)}/
		let imgPath = ""
		if (data.match(pathRegex)) {
			imgPath = data.match(pathRegex)[1]
		}

		const altRegex = /alt={([\s\S]+?)}/
		let alt = ""
		if (data.match(altRegex)) {
			alt = data.match(altRegex)[1]
		}

		const widthRegex = /width={([\s\S]+?)}/
		let width = null
		if (data.match(widthRegex)) {
			width = data.match(widthRegex)[1]
		}

		const animatedGifRegex = /animatedGif={([\s\S]+?)}/
		let noProcessing = false
		if (data.match(animatedGifRegex)) {
			noProcessing = true
		}

		const flexibleHeightRegex = /flexibleHeight={([\s\S]+?)}/
		let flexibleHeight = false
		if (data.match(flexibleHeightRegex)) {
			flexibleHeight = true
		}

		const clickThruRegex = /clickThru={([\s\S]+?)}/
		let clickThru = ""
		if (data.match(clickThruRegex)) {
			clickThru = data.match(clickThruRegex)[1]
		}

		const printRegex = /print={([\s\S]+?)}/
		let print = ""
		if (data.match(printRegex)) {
			print = data.match(printRegex)[1]
		}

		const imageContents = (
			<>
				<CDNImage imagePath={imgPath} alt={alt} fit={"max"} width={1200} height={850}
						  flexibleHeight={flexibleHeight} noProcessing={noProcessing} showUndownloadedMessage={true} />
				{(print.length > 0) ? (
					<Link
						to={`/product/${print}`}
						className="image__buy-a-print"
					>
						{ShoppingBagSvg}
						{" "}
						<span className="label">Buy as a Print</span>
					</Link>
				) : null}
			</>
		);

		if (imgPath.length > 0) {
			return (
				<div
					className="essay__image__wrapper"
					style={{ maxWidth: `${width}px` }}
				>
					{clickThru.length > 0 ? (
						<a href={clickThru}>{imageContents}</a>
					) : (
						<>{imageContents}</>
					)}
				</div>
			);
		}

		return null;
	}

	buildDoubleImage(data, i) {
		const imageRegex = /{image[\s\S]+?endimage}/g;

		let imageMatches = [];
		let m;
		do {
			m = imageRegex.exec(data);
			if (m) {
				imageMatches.push(m);
			}
		} while (m);

		const captionRegex = /caption={([\s\S]*?)}/;
		const captionMatches = imageMatches.map(match => {
			if (match[0].match(captionRegex)) {
				return match[0].match(captionRegex)[1];
			}
			return null;
		});

		return (
			<div id={`img-${i}`} className="essay__double-image">
				<div className="essay__center-content">
					<div className="essay__double-image__content">
						<a className="essay__anchor-link" href={`#img-${i}`}>{LinkSvg}</a>
						<div className="essay__double-image__image">
							{this.formatImage(imageMatches[0][0])}
							{captionMatches[0] && (
								<div
									className="essay__double-image__caption"
									dangerouslySetInnerHTML={{ __html: marked(captionMatches[0]) }}
								/>
							)}
						</div>
						<div className="essay__double-image__image">
							{this.formatImage(imageMatches[1][0])}
							{captionMatches[1] && (
								<div
									className="essay__double-image__caption"
									dangerouslySetInnerHTML={{ __html: marked(captionMatches[1]) }}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	grabFootnoteReferences(essayText) {
		const footnoteAnchorRegex = /\[\^([\S]+?)\]/g;
		let anchorMatches = [];
		let m;

		do {
			m = footnoteAnchorRegex.exec(essayText);
			if (m) {
				anchorMatches.push(m);
			}
		} while (m);

		return Array.from(anchorMatches).map(match => match[1]);
	}

	insertFootnoteAnchors(essayText) {
		let processedText = essayText;

		this.footnoteReferences.forEach((val, idx) => {
			const n = idx + 1;
			const footnoteLink = `<sup class="footnote"><a id="ref${n}" href="#fn${n}">${n}</a></sup>`;
			processedText = processedText.replace(`[^${val}]`, footnoteLink);
		});

		return processedText;
	}

	buildNote(block, i) {
		const contentRegex = /contents={([\S\s]+?)}/;
		let content = '';
		if (block.match(contentRegex)) {
			content = block.match(contentRegex)[1];
		}
		return (
			<div className="essay__text-block" id={`${i}`} key={`note-${i}`}>
				<div className="essay__left-bar"></div>
				<div className="essay__center-content">
					<a className="essay__anchor-link" aria-hidden="true" tabindex="-1" href={`#${i}`}>{ LinkSvg }</a>
					<div className="note-block" dangerouslySetInnerHTML={{ __html: marked(content) }} />
				</div>
			</div>
		);
	}

	processTextBlock(block, i, indexItems) {
		let output = marked(block);
		output = output.split('\n');

		output = output.filter(line => line.length > 0);

		const pClassesRegex = /^<p class="([\S\s]+?)">/;
		const iframeRegex = /^<iframe/;
		const mediaEmbedRegex = /^<div class="media-embed-block">/;
		return output.map((line, idx) => {
			if (iframeRegex.exec(line) || mediaEmbedRegex.exec(line)) {
				return (
					<div
						className="essay__iframe-block"
						dangerouslySetInnerHTML={{ __html: line }}
					/>
				);
			}

			let classes = '';
			if (pClassesRegex.exec(line)) {
				classes = pClassesRegex.exec(line)[1];
			}

			if (indexItems && indexItems.length > 0) {
				let indexed = false;
				indexItems.forEach(item => {
					if (item.paragraphRef === `p-${i}-${idx}`) {
						indexed = true;
					}
				});

				if (indexed) {
					line = line.substring(0, line.length - 4) + ` <span class="index-button" id="index-button-${i}-${idx}" role="button">※</span></p>`;
				}
			}

			return (
				<div className={`essay__text-block ${classes}`} key={`${i}-${idx}`}>
					<div className="floating-anchor" id={`${i}-${idx}`} />
					<div className="floating-anchor" id={`p-${i}-${idx}`} />
					<div className="essay__left-bar"></div>
					<div className="essay__center-content">
						{(!this.props.suppressAnchors) ? (
							<a className="essay__anchor-link" aria-hidden="true" tabindex="-1" href={`#p-${i}-${idx}`}>{ LinkSvg }</a>
						) : null}
						<div dangerouslySetInnerHTML={{ __html: marked(line) }} />
					</div>
					<div className="essay__right-bar">
						{indexItems && indexItems.length > 0 && (
							<IndexSidebar
								paragraph={`p-${i}-${idx}`}
								indexItems={indexItems}
							/>
						)}
					</div>
				</div>
			);
		});
	}

	processedEssay(essayText, indexItems, suppressInterstitials) {
		if (!essayText || essayText.length === 0) {
			return null;
		}

		let processedText = this.insertFootnoteAnchors(essayText);

		const specialBlockRegex = /{2image[\s\S]+?end2image}|{image[\s\S]+?endimage}|{note[\s\S]+?endnote}|{sidebyside[\s\S]+?endsidebyside}/g;
		const textContent = processedText.split(specialBlockRegex);
		let specialBlockMatches = [];
		let m;
		do {
			m = specialBlockRegex.exec(processedText);
			if (m) {
				specialBlockMatches.push(m);
			}
		} while (m);
		const specialBlocks = Array.from(specialBlockMatches).map(match => match[0]);

		let contentBlocks = [];

		// Weave together
		const imageRegex = /^{image/;
		const doubleImageRegex = /^{2image/;
		const noteRegex = /^{note/;
		const sideBySideRegex = /^{sidebyside/;
		for (let i = 0; i < textContent.length; i++) {
			contentBlocks = contentBlocks.concat(this.processTextBlock(textContent[i], i, indexItems));
			if (i < textContent.length - 1) {
				if (imageRegex.exec(specialBlocks[i])) {
					contentBlocks.push(this.buildImage(specialBlocks[i], i));
				} else if (doubleImageRegex.exec(specialBlocks[i])) {
					contentBlocks.push(this.buildDoubleImage(specialBlocks[i], i));
				} else if (noteRegex.exec(specialBlocks[i])) {
					contentBlocks.push(this.buildNote(specialBlocks[i], i));
				} else if (sideBySideRegex.exec(specialBlocks[i])) {
					contentBlocks.push(this.buildSideBySide(specialBlocks[i], i));
				}
			}
		}

		return contentBlocks;
	}

	render() {
		return (
			<>
				{this.processedEssay(this.props.essayText, this.props.indexItems, this.props.suppressInterstitials)}
			</>
		);
	}
}
